// OFD API - Barrel Export

// Interfaces
export type { IInterestClient } from './IInterestClient';
export type { IOnboardingClient } from './IOnboardingClient';
export type { IOnboardingDocumentClient } from './IOnboardingDocumentClient';
export type { IProductDocumentsClient } from './IProductDocumentsClient';

// Real API Clients
export { InterestClient } from './interestClient';
export { OnboardingClient } from './onboardingClient';
export { OnboardingDocumentClient } from './onboardingDocumentClient';
export { ProductDocumentsClient } from './productDocumentsClient';

// Mocked Clients
export { InterestClientMocked } from './interestClientMocked';
export { OnboardingClientMocked } from './onboardingClientMocked';
export { OnboardingDocumentClientMocked } from './onboardingDocumentClientMocked';
export { ProductDocumentsClientMocked } from './productDocumentsClientMocked';

// Types
export type {
  ConfigurationModel,
  ContactDetails,
  Int32StringKeyValuePair,
  InterestFormModel,
  ItemAlternateId,
  OnboardingCataloguesModel,
  OnboardingPageData,
  Order,
  OrderItem,
  ProductDocumentModel,
  RetailerDeliveryLocationModel,
  RetailerDocumentModel,
  RetailerEdnModel,
  RetailerHeaderModel,
  DeliveryMethodModel,
  RetailerIdentifierModel,
  RetailerListModel,
  DeliveryMethodListModel,
  RetailerOnboardingDataModel,
  UserFranchiseList,
  FileResponse,
} from './ofdTypes';

// Utilities
export { OfdApiException, throwException, isAxiosError } from './ofdUtils';

/* ---- Configuration ---- */

import { shouldUseMockedClient } from '../../environment';

/**
 * Determines whether to use mocked clients.
 * Automatically uses mocked clients on localhost and ofstest5 environments.
 */
const isMockedEnvironment = () => shouldUseMockedClient();

/* ---- Factory Functions ---- */

import { IInterestClient } from './IInterestClient';
import { IOnboardingClient } from './IOnboardingClient';
import { IOnboardingDocumentClient } from './IOnboardingDocumentClient';
import { IProductDocumentsClient } from './IProductDocumentsClient';
import { InterestClient } from './interestClient';
import { OnboardingClient } from './onboardingClient';
import { OnboardingDocumentClient } from './onboardingDocumentClient';
import { ProductDocumentsClient } from './productDocumentsClient';
import { InterestClientMocked } from './interestClientMocked';
import { OnboardingClientMocked } from './onboardingClientMocked';
import { OnboardingDocumentClientMocked } from './onboardingDocumentClientMocked';
import { ProductDocumentsClientMocked } from './productDocumentsClientMocked';

let interestClientInstance: IInterestClient | null = null;
let onboardingClientInstance: IOnboardingClient | null = null;
let onboardingDocumentClientInstance: IOnboardingDocumentClient | null = null;
let productDocumentsClientInstance: IProductDocumentsClient | null = null;

/**
 * Get the InterestClient instance.
 * Automatically uses mocked data on localhost and ofstest5 environments.
 */
export function getInterestClient(baseUrl?: string): IInterestClient {
  if (interestClientInstance) {
    return interestClientInstance;
  }

  if (isMockedEnvironment()) {
    interestClientInstance = new InterestClientMocked();
  } else {
    interestClientInstance = new InterestClient(baseUrl);
  }
  return interestClientInstance;
}

/**
 * Get the OnboardingClient instance.
 * Automatically uses mocked data on localhost and ofstest5 environments.
 */
export function getOnboardingClient(baseUrl?: string): IOnboardingClient {
  if (onboardingClientInstance) {
    return onboardingClientInstance;
  }

  if (isMockedEnvironment()) {
    onboardingClientInstance = new OnboardingClientMocked();
  } else {
    onboardingClientInstance = new OnboardingClient(baseUrl);
  }
  return onboardingClientInstance;
}

/**
 * Get the OnboardingDocumentClient instance.
 * Automatically uses mocked data on localhost and ofstest5 environments.
 */
export function getOnboardingDocumentClient(baseUrl?: string): IOnboardingDocumentClient {
  if (onboardingDocumentClientInstance) {
    return onboardingDocumentClientInstance;
  }

  if (isMockedEnvironment()) {
    onboardingDocumentClientInstance = new OnboardingDocumentClientMocked();
  } else {
    onboardingDocumentClientInstance = new OnboardingDocumentClient(baseUrl);
  }
  return onboardingDocumentClientInstance;
}

/**
 * Get the ProductDocumentsClient instance.
 * Automatically uses mocked data on localhost and ofstest5 environments.
 */
export function getProductDocumentsClient(baseUrl?: string): IProductDocumentsClient {
  if (productDocumentsClientInstance) {
    return productDocumentsClientInstance;
  }

  if (isMockedEnvironment()) {
    productDocumentsClientInstance = new ProductDocumentsClientMocked();
  } else {
    productDocumentsClientInstance = new ProductDocumentsClient(baseUrl);
  }
  return productDocumentsClientInstance;
}

/**
 * Reset all client instances (useful for testing).
 */
export function resetOfdClients(): void {
  interestClientInstance = null;
  onboardingClientInstance = null;
  onboardingDocumentClientInstance = null;
  productDocumentsClientInstance = null;
}
